/**
 * Offline action queue — persists mutating API calls to localStorage so they
 * survive page refresh, and drains them sequentially as soon as connectivity
 * comes back.
 *
 * Used by `api.js` (axios interceptor) when a POST/PATCH/DELETE call fails
 * with a network error AND the caller opted-in via `config.offlineQueueable`.
 *
 * Stored shape (localStorage key `prerec.offline_queue`):
 *   [
 *     {
 *       id: "qa_<random>",
 *       at: 1716200000000,
 *       method: "POST",
 *       path: "/candidates/<id>/no-answer",
 *       body: { ... },
 *       label: "Pas de réponse — Sarah Ben Ali",
 *       attempts: 0,
 *       lastError: null,
 *     },
 *   ]
 */
import axios from "axios";

import { tokenStore } from "./api";
import { isOnline, subscribeConnectivity } from "./connectivity";

const KEY = "prerec.offline_queue";
const MAX_ATTEMPTS = 5;
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const listeners = new Set();

function read() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const data = JSON.parse(raw);
        return Array.isArray(data) ? data : [];
    } catch (_e) { return []; }
}

function write(items) {
    try { localStorage.setItem(KEY, JSON.stringify(items)); }
    catch (_e) { /* quota — ignore */ }
}

function notify(extras = {}) {
    const queue = read();
    listeners.forEach((cb) => {
        try { cb({ count: queue.length, queue, ...extras }); }
        catch (_e) { /* swallow */ }
    });
}

function genId() {
    return `qa_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}


/**
 * Push a mutating action into the queue.
 *
 * @param {Object} action
 * @param {string} action.method — "POST" | "PATCH" | "DELETE" | "PUT"
 * @param {string} action.path   — path starting with "/" (e.g. "/candidates/x/no-answer")
 * @param {Object} [action.body]
 * @param {string} [action.label] — human label shown in the banner
 * @returns {Object} the persisted item
 */
export function enqueueAction({ method, path, body, label }) {
    const item = {
        id: genId(),
        at: Date.now(),
        method,
        path,
        body: body ?? null,
        label: label || `${method} ${path}`,
        attempts: 0,
        lastError: null,
    };
    const queue = read();
    queue.push(item);
    write(queue);
    notify();
    // Kick off a drain attempt in case we became online in the meantime
    queueMicrotask(() => { drainOnce(); });
    return item;
}

/** Subscribe to queue changes. Cb signature: ({count, queue, syncing?, just_synced_id?}) => void. */
export function subscribeQueue(cb) {
    listeners.add(cb);
    const queue = read();
    cb({ count: queue.length, queue });
    return () => listeners.delete(cb);
}

export function getQueue() {
    return read();
}

export function clearAll() {
    write([]);
    notify();
}


let draining = false;

/** Drain the queue once. Safe to call concurrently — re-entrancy guarded. */
export async function drainOnce() {
    if (draining) return;
    if (!isOnline()) return;
    const queue = read();
    if (queue.length === 0) return;

    draining = true;
    notify({ syncing: true });

    try {
        for (const item of queue) {
            try {
                const token = tokenStore.get();
                await axios.request({
                    baseURL: API_BASE,
                    url: item.path,
                    method: item.method,
                    data: item.body,
                    timeout: 30_000,
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                // Success → remove from queue
                const remaining = read().filter((q) => q.id !== item.id);
                write(remaining);
                notify({ just_synced_id: item.id });
            } catch (err) {
                // Network error or backend rejection
                const offline = !err.response;
                const status = err.response?.status;

                if (offline) {
                    // Stop draining — connectivity lost again, will retry next cycle
                    break;
                }
                // 4xx (other than 401) → drop the action with an error (won't retry forever)
                if (status && status >= 400 && status < 500 && status !== 401) {
                    const remaining = read().filter((q) => q.id !== item.id);
                    write(remaining);
                    notify({
                        failed_id: item.id,
                        failed_label: item.label,
                        failed_status: status,
                        failed_message: err.response?.data?.detail || err.message,
                    });
                } else {
                    // 5xx or 401 → bump attempts, stop draining, retry later
                    const next = read().map((q) =>
                        q.id === item.id
                            ? { ...q, attempts: (q.attempts || 0) + 1, lastError: err.message }
                            : q,
                    );
                    write(next);
                    // After MAX_ATTEMPTS, drop and surface to UI
                    if ((item.attempts || 0) + 1 >= MAX_ATTEMPTS) {
                        const remaining = read().filter((q) => q.id !== item.id);
                        write(remaining);
                        notify({
                            failed_id: item.id,
                            failed_label: item.label,
                            failed_status: status || 0,
                            failed_message: `Échec après ${MAX_ATTEMPTS} tentatives`,
                        });
                    }
                    break;
                }
            }
        }
    } finally {
        draining = false;
        notify({ syncing: false });
    }
}

// Auto-drain whenever connectivity becomes available
subscribeConnectivity(({ online }) => {
    if (online) drainOnce();
});

// Auto-drain on tab focus (catches "fell asleep" laptops)
if (typeof window !== "undefined") {
    window.addEventListener("focus", () => { drainOnce(); });
}
